import { useMemo, useState } from 'react'
import { type LocationRow, useLocations } from '../../../features/locations/use-locations'
import { SearchIcon } from './icons'
import { StatusPill } from './status-pill'

const th =
  'sticky top-0 z-10 h-10 px-5 text-left text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--color-muted)] backdrop-blur-[12px]'

const SKELETON_ROWS = Array.from({ length: 8 }, (_, i) => ({ id: `sk-${i}`, width: 40 + ((i * 17) % 35) }))

function Skeleton() {
  return (
    <tbody aria-hidden="true">
      {SKELETON_ROWS.map((s) => (
        <tr key={s.id} className="h-11 border-t border-[var(--color-border)]">
          <td className="px-5">
            <div className="v1-skeleton h-3.5 w-14" />
          </td>
          <td className="px-5">
            <div className="v1-skeleton h-3.5" style={{ width: `${s.width}%` }} />
          </td>
          <td className="px-5">
            <div className="v1-skeleton h-5 w-16 rounded-full" />
          </td>
        </tr>
      ))}
    </tbody>
  )
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <tbody>
      <tr>
        <td colSpan={3} className="px-5 py-16 text-center">
          <p className="text-[15px] font-medium">{title}</p>
          <p className="mt-1 text-[13px] text-[var(--color-muted)]">{body}</p>
        </td>
      </tr>
    </tbody>
  )
}

export function PorcelainLocations() {
  const { data, isLoading, error } = useLocations()
  const [query, setQuery] = useState('')

  const rows: LocationRow[] = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!data) return []
    return q ? data.filter((r) => r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)) : data
  }, [data, query])

  return (
    <section>
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-[30px] font-semibold leading-tight tracking-[-0.01em]">Locations</h1>
          <p className="mt-1.5 text-[15px] text-[var(--color-muted)]">
            {data ? `${data.length} stores across the banner.` : 'Every store, at a glance.'}
          </p>
        </div>
        <label className="relative block w-[260px]">
          <span className="sr-only">Search locations</span>
          <SearchIcon
            width={16}
            height={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-muted)]"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code or name"
            className="h-9 w-full rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] pl-9 pr-3 text-[14px] text-[var(--color-fg)] outline-none transition-shadow placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:shadow-[0_0_0_3px_var(--v1-accent-soft)]"
          />
        </label>
      </div>

      <div
        className="mt-8 overflow-hidden rounded-[12px] border border-[var(--color-border)] bg-[var(--color-surface)]"
        style={{ boxShadow: 'var(--v1-shadow)' }}
      >
        <table className="w-full border-collapse text-[14px]" aria-busy={isLoading}>
          <thead style={{ background: 'color-mix(in srgb, var(--color-surface) 80%, transparent)' }}>
            <tr>
              <th className={`${th} w-[140px]`}>Store</th>
              <th className={th}>Name</th>
              <th className={`${th} w-[140px]`}>Status</th>
            </tr>
          </thead>
          {isLoading ? (
            <Skeleton />
          ) : error ? (
            <Message title="We couldn't load locations" body="Check your connection and try again in a moment." />
          ) : rows.length === 0 ? (
            <Message
              title={query ? 'No matches' : 'No locations yet'}
              body={query ? `Nothing matches "${query}".` : 'Locations will appear here once they are available.'}
            />
          ) : (
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r.code}-${i}`}
                  className="h-11 border-t border-[var(--color-border)] transition-colors hover:bg-[var(--v1-accent-soft)]"
                >
                  <td
                    className="px-5 text-[13px] text-[var(--color-muted)]"
                    style={{ fontFamily: 'var(--v1-font-mono)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {r.code}
                  </td>
                  <td className="px-5 font-medium">{r.name}</td>
                  <td className="px-5">
                    <StatusPill status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    </section>
  )
}
